import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Flag, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface CountryTemplate {
  country: string;
  region: string;
  flag: string;
  name: string;
  description: string;
  regulatory_authority: string;
  official_website: string;
  critical_limits: any;
  monitoring_frequency: any;
  required_signatures: string[];
}

const countryTemplates: CountryTemplate[] = [
  {
    country: 'UK',
    region: 'United Kingdom',
    flag: '🇬🇧',
    name: 'UK Food Safety & Hygiene Regulations',
    description: 'Compliance with FSA (Food Standards Agency) requirements, including HACCP principles and Regulation (EC) No 852/2004.',
    regulatory_authority: 'Food Standards Agency (FSA)',
    official_website: 'https://www.food.gov.uk',
    critical_limits: {
      cold_storage: { min: 0, max: 8, unit: '°C' },
      hot_holding: { min: 63, max: null, unit: '°C' },
      cooking_temp: { min: 75, max: null, unit: '°C' },
      reheating_temp: { min: 75, max: null, unit: '°C' },
      freezer_storage: { min: -18, max: null, unit: '°C' }
    },
    monitoring_frequency: {
      temperature_checks: 'Every 4 hours',
      cleaning_verification: 'Daily',
      equipment_calibration: 'Quarterly'
    },
    required_signatures: ['Food Safety Manager', 'Duty Manager', 'Staff Member']
  },
  {
    country: 'Norway',
    region: 'Norway',
    flag: '🇳🇴',
    name: 'Norwegian Food Safety Regulations (Mattilsynet)',
    description: 'Compliance with Mattilsynet requirements and Norwegian food safety legislation.',
    regulatory_authority: 'Mattilsynet (Norwegian Food Safety Authority)',
    official_website: 'https://www.mattilsynet.no',
    critical_limits: {
      cold_storage: { min: 0, max: 4, unit: '°C' },
      hot_holding: { min: 65, max: null, unit: '°C' },
      cooking_temp: { min: 75, max: null, unit: '°C' },
      reheating_temp: { min: 75, max: null, unit: '°C' },
      freezer_storage: { min: -18, max: null, unit: '°C' }
    },
    monitoring_frequency: {
      temperature_checks: 'Every 2 hours',
      cleaning_verification: 'Daily',
      equipment_calibration: 'Quarterly'
    },
    required_signatures: ['Daglig leder', 'Kjøkkensjef', 'Ansvarlig']
  },
  {
    country: 'Germany',
    region: 'Germany',
    flag: '🇩🇪',
    name: 'German Food Hygiene Regulations (LMHV)',
    description: 'Compliance with BVL (Federal Office of Consumer Protection) and EU Regulation 852/2004.',
    regulatory_authority: 'Bundesamt für Verbraucherschutz und Lebensmittelsicherheit (BVL)',
    official_website: 'https://www.bvl.bund.de',
    critical_limits: {
      cold_storage: { min: 0, max: 7, unit: '°C' },
      hot_holding: { min: 65, max: null, unit: '°C' },
      cooking_temp: { min: 72, max: null, unit: '°C' },
      reheating_temp: { min: 72, max: null, unit: '°C' },
      freezer_storage: { min: -18, max: null, unit: '°C' }
    },
    monitoring_frequency: {
      temperature_checks: 'Every 3 hours',
      cleaning_verification: 'Daily',
      equipment_calibration: 'Bi-annually'
    },
    required_signatures: ['Betriebsleiter', 'Küchenchef', 'Mitarbeiter']
  },
  {
    country: 'France',
    region: 'France',
    flag: '🇫🇷',
    name: 'French Food Safety Regulations (DGCCRF)',
    description: 'Compliance with Direction Générale de la Concurrence requirements and EU hygiene regulations.',
    regulatory_authority: 'Direction Générale de la Concurrence, de la Consommation et de la Répression des Fraudes (DGCCRF)',
    official_website: 'https://www.economie.gouv.fr/dgccrf',
    critical_limits: {
      cold_storage: { min: 0, max: 4, unit: '°C' },
      hot_holding: { min: 63, max: null, unit: '°C' },
      cooking_temp: { min: 75, max: null, unit: '°C' },
      reheating_temp: { min: 75, max: null, unit: '°C' },
      freezer_storage: { min: -18, max: null, unit: '°C' }
    },
    monitoring_frequency: {
      temperature_checks: 'Toutes les 2 heures',
      cleaning_verification: 'Quotidien',
      equipment_calibration: 'Trimestriel'
    },
    required_signatures: ['Responsable', 'Chef de cuisine', 'Personnel']
  },
  {
    country: 'Sweden',
    region: 'Sweden',
    flag: '🇸🇪',
    name: 'Swedish Food Safety Regulations (Livsmedelsverket)',
    description: 'Compliance with Livsmedelsverket requirements and Swedish food safety legislation.',
    regulatory_authority: 'Livsmedelsverket (Swedish Food Agency)',
    official_website: 'https://www.livsmedelsverket.se',
    critical_limits: {
      cold_storage: { min: 0, max: 6, unit: '°C' },
      hot_holding: { min: 63, max: null, unit: '°C' },
      cooking_temp: { min: 75, max: null, unit: '°C' },
      reheating_temp: { min: 75, max: null, unit: '°C' },
      freezer_storage: { min: -18, max: null, unit: '°C' }
    },
    monitoring_frequency: {
      temperature_checks: 'Varje 3:e timme',
      cleaning_verification: 'Dagligen',
      equipment_calibration: 'Kvartalsvis'
    },
    required_signatures: ['Verksamhetsansvarig', 'Kökschef', 'Personal']
  },
  {
    country: 'Denmark',
    region: 'Denmark',
    flag: '🇩🇰',
    name: 'Danish Food Safety Regulations (Fødevarestyrelsen)',
    description: 'Compliance with Fødevarestyrelsen requirements and Danish food safety legislation.',
    regulatory_authority: 'Fødevarestyrelsen (Danish Veterinary and Food Administration)',
    official_website: 'https://www.foedevarestyrelsen.dk',
    critical_limits: {
      cold_storage: { min: 0, max: 5, unit: '°C' },
      hot_holding: { min: 65, max: null, unit: '°C' },
      cooking_temp: { min: 75, max: null, unit: '°C' },
      reheating_temp: { min: 75, max: null, unit: '°C' },
      freezer_storage: { min: -18, max: null, unit: '°C' }
    },
    monitoring_frequency: {
      temperature_checks: 'Hver 2. time',
      cleaning_verification: 'Dagligt',
      equipment_calibration: 'Kvartalsvis'
    },
    required_signatures: ['Ansvarlig leder', 'Køkkenchef', 'Medarbejder']
  }
];

interface QuickCreateLawPackWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const QuickCreateLawPackWizard = ({ open, onOpenChange, onSuccess }: QuickCreateLawPackWizardProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState<CountryTemplate | null>(null);
  const [creating, setCreating] = useState(false);

  const handleCreateLawPack = async () => {
    if (!selectedTemplate || !user) return;

    setCreating(true);
    try {
      // Get user's tenant_id
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .single();

      if (!userRoles?.tenant_id) {
        throw new Error('User tenant not found');
      }

      const { error } = await supabase
        .from('law_packs')
        .insert({
          name: selectedTemplate.name,
          region: selectedTemplate.region,
          description: selectedTemplate.description,
          regulatory_authority: selectedTemplate.regulatory_authority,
          official_website: selectedTemplate.official_website,
          critical_limits: selectedTemplate.critical_limits,
          monitoring_frequency: selectedTemplate.monitoring_frequency,
          required_signatures: selectedTemplate.required_signatures,
          tenant_id: userRoles.tenant_id,
          version: '1.0',
          is_active: true,
          external_links: [
            {
              title: 'Regulatory Authority Website',
              url: selectedTemplate.official_website,
              type: 'guidance',
              description: 'Official food safety regulations and guidance'
            }
          ],
          source_documents: [],
          legal_citations: [],
          critical_control_points: [],
          change_log: [
            {
              date: new Date().toISOString(),
              version: '1.0',
              changes: 'Initial law pack created from regional template'
            }
          ]
        });

      if (error) throw error;

      toast({
        title: "Law pack created successfully",
        description: `${selectedTemplate.name} has been added to your law packs.`
      });

      onSuccess();
      onOpenChange(false);
      setSelectedTemplate(null);
    } catch (error: any) {
      console.error('Error creating law pack:', error);
      toast({
        title: "Error creating law pack",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5" />
            Quick Create Law Pack
          </DialogTitle>
          <DialogDescription>
            Select a country to instantly create a law pack with pre-configured regulations and critical limits
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {!selectedTemplate ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {countryTemplates.map((template) => (
                <Card 
                  key={template.country}
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => setSelectedTemplate(template)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-4xl">{template.flag}</span>
                      <Badge variant="secondary">{template.country}</Badge>
                    </div>
                    <h3 className="font-semibold mb-2">{template.region}</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {template.regulatory_authority}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="text-xs">
                        {Object.keys(template.critical_limits).length} Critical Limits
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {template.required_signatures.length} Signatures
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <span className="text-5xl">{selectedTemplate.flag}</span>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-1">{selectedTemplate.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedTemplate.region}</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedTemplate(null)}
                >
                  Change
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Description
                  </h4>
                  <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Regulatory Authority</h4>
                  <p className="text-sm text-muted-foreground">{selectedTemplate.regulatory_authority}</p>
                  <a 
                    href={selectedTemplate.official_website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    {selectedTemplate.official_website}
                  </a>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Critical Limits</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(selectedTemplate.critical_limits).map(([key, value]: [string, any]) => (
                      <div key={key} className="p-3 bg-muted rounded-lg">
                        <p className="text-xs font-medium text-muted-foreground mb-1 capitalize">
                          {key.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm font-semibold">
                          {value.min !== null && value.min !== undefined ? `${value.min}°C` : ''} 
                          {value.min !== null && value.max !== null ? ' - ' : ''}
                          {value.max !== null && value.max !== undefined ? `${value.max}°C` : ''}
                          {value.min !== null && value.max === null ? '+ (minimum)' : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Required Signatures</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.required_signatures.map((sig) => (
                      <Badge key={sig} variant="secondary">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {sig}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedTemplate(null)}
                  disabled={creating}
                >
                  Back
                </Button>
                <Button 
                  onClick={handleCreateLawPack}
                  disabled={creating}
                  className="cta-primary"
                >
                  {creating ? 'Creating...' : 'Create Law Pack'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
